import { component } from 'tsdi';
import { wrapRequest } from 'wrap-request';
import { makeAutoObservable } from 'mobx';
import { injectTSDI } from '../../core/tsdi';
import { Api } from '../../core/api';
import { initializeConfigStyles } from '../../core/initialize-config-styles';
import { ConnectApiStudioDtoV2 } from '../../core/api/dtos';
import { initializeLocale } from '../../core/initialize-locale';
import { initializeAnalytics } from '../../core/analytics';
import { initializeFavicon } from '../../core/initialize-favicon';
import { StudioStore } from '../../core/studio-store';
import { errorCode } from '../../components/error-handler';
import { findDisplayValues } from '../../core/multi-language';

@component
export class CheckoutConfigStore {
    constructor() {
        makeAutoObservable(this);
    }

    private studioStore = injectTSDI(StudioStore);

    public url = '';

    public configRequest = wrapRequest(
        async (pageIdentifier: string) => {
            const api = injectTSDI(Api);

            const checkout =
                await api.client.ConnectApiOnlinePagesController.getOnlinePagesCheckoutConfigurationByUrl(
                    pageIdentifier
                );

            if (!checkout.activatedFacilityIds?.length) {
                throw errorCode('SHNC');
            }

            if (!checkout.basicConfigurationId) {
                throw new Error('Basic configuration id missing');
            }

            const basic =
                await api.client.ConnectApiOnlinePagesController.getOnlinePagesBasicConfigurationById(
                    checkout.basicConfigurationId
                );

            initializeLocale(basic?.locale || 'de-DE');
            initializeAnalytics(basic?.trackingIdDto);
            initializeFavicon(basic.favicon);
            await initializeConfigStyles(basic, checkout.customCssUrl);

            return {
                checkout,
                basic
            };
        },
        {
            defaultData: {
                basic: {},
                checkout: {
                    locationOverviewPageConfigDto: { logoLinkDto: {} },
                    name: '',
                    rateBundlesPageConfigDto: {
                        logoLinkDto: {},
                        postAction: 'SHOW_POPUP'
                    },
                    trialSessionPageConfigDto: {
                        fitnessGoalsDto: {},
                        logoLinkDto: {},
                        postAction: 'SHOW_POPUP'
                    },
                    url: ''
                }
            }
        }
    );

    private get config() {
        const { result } = this.configRequest;

        if (!result) {
            throw new Error('Checkout page config not fetched yet');
        }

        return result;
    }

    public get configId() {
        return this.config.checkout.databaseId;
    }

    public get activatedFacilityIds() {
        if (!this.config.checkout.activatedFacilityIds?.length) {
            throw errorCode('SHNC');
        }
        return this.config.checkout.activatedFacilityIds ?? [];
    }

    public get germanLegalStudio() {
        return (
            this.config.checkout.locationOverviewPageConfigDto
                ?.legalOrganizationUnitDto?.addressDto?.country === 'DE'
        );
    }

    public get logoUrl() {
        return this.config.basic.logoUrl ?? '';
    }

    public get trackingId() {
        return this.config.basic?.trackingIdDto ?? {};
    }

    public get linksPosition() {
        return this.config.basic.legalLinksPosition;
    }

    public get showStudioAddress() {
        return this.config.checkout.showAddress;
    }

    public get locationOverviewConfig() {
        const locationOverviewPageConfigDto =
            this.config.checkout.locationOverviewPageConfigDto;

        return locationOverviewPageConfigDto ?? {};
    }

    public get rateBundlesConfig() {
        const rateBundlesPageConfigDto =
            this.config.checkout.rateBundlesPageConfigDto;
        return rateBundlesPageConfigDto ?? {};
    }

    public get rateBundlesPostActionConfig() {
        return {
            ...this.rateBundlesConfig,
            onCloseAfterSuccess: this.reload
        };
    }

    public get trialSessionConfig() {
        const trialSessionPageConfigDto =
            this.config.checkout.trialSessionPageConfigDto;
        return trialSessionPageConfigDto ?? {};
    }

    public get fitnessGoalsOptions() {
        const { fitnessGoalsDto } = this.trialSessionConfig;
        if (
            !fitnessGoalsDto?.enabled ||
            !fitnessGoalsDto?.localizedGoals?.length
        ) {
            return undefined;
        }
        return findDisplayValues(fitnessGoalsDto?.localizedGoals).map(
            (goal) => ({
                value: goal,
                label: goal
            })
        );
    }

    public get theme() {
        return this.configRequest.result?.basic.theme;
    }

    public studioActiveForConfiguration = (
        studioOrStudioId: number | ConnectApiStudioDtoV2
    ) => {
        const id =
            typeof studioOrStudioId === 'number'
                ? studioOrStudioId
                : studioOrStudioId.id;
        return this.activatedFacilityIds.includes(id);
    };

    public reload = () => {
        window.location.pathname = this.url;
    };

    public getLocationOverviewUrl = () => this.url;

    public getTrialSessionViewUrl = (studioId: number | 'current') => {
        const id = this.studioStore.resolveStudioId(studioId);
        return `${this.url}/trial-session/${id}`;
    };

    public getRateBundlesViewUrl = (studioId: number | 'current') => {
        const id = this.studioStore.resolveStudioId(studioId);
        return `${this.url}/rates/${id}`;
    };

    public hasLocationOverview = () => this.locationOverviewConfig.enabled;

    public studioHasRateBundlesView = (
        studio: ConnectApiStudioDtoV2 | 'current'
    ) => {
        const { hasRateBundles } = this.studioStore.resolveStudio(studio);
        return hasRateBundles && this.rateBundlesConfig.enabled;
    };

    public studioHasTrialSessionView = (
        studio: ConnectApiStudioDtoV2 | 'current'
    ) => {
        const { trialSessionBookable } = this.studioStore.resolveStudio(studio);
        return trialSessionBookable && this.trialSessionConfig.enabled;
    };

    public studioGoogleAnalyticsId = (studioId: number) =>
        this.config.basic.trackingIdDto?.googleAnalyticsAccountNumberAdjustments?.adjustmentEntries?.find(
            (dto) => dto.facilityId === studioId
        )?.value ??
        this.config.basic.trackingIdDto?.googleAnalyticsAccountNumber;

    public studioFacebookPixelId = (studioId: number) =>
        this.config.basic.trackingIdDto?.facebookPixelIdAdjustments?.adjustmentEntries?.find(
            (dto) => dto.facilityId === studioId
        )?.value ?? this.config.basic.trackingIdDto?.facebookPixelId;
}
